import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useLocale } from "../../shared/i18n/LocaleContext";
import { PageLoading } from "../../shared/components/PageLoading";
import {
  changeCommercialStatus,
  getCommercial,
  saveCommercial,
  type CommercialRecord,
  type CommercialSnapshot,
} from "./commercialApi";
import type { TranslationKey } from "../../shared/i18n/translations";
import { CommercialRecordPage } from "./CommercialRecordPage";
import { ClientRecordListControls } from "../../shared/components/ClientRecordListControls";
import { useClientRecordList } from "../../shared/components/useClientRecordList";
import { SortableHeader } from "../../shared/components/SortableHeader";
import { AppModal } from "../../shared/components/AppModal";
import { DocumentTemplateDesigner } from "./DocumentTemplateDesigner";

export type CommercialModule = "sales" | "purchasing";
export type CommercialSection =
  | "overview"
  | "customers"
  | "customerContacts"
  | "customerAddresses"
  | "suppliers"
  | "supplierContacts"
  | "supplierAddresses"
  | "supplierArticles"
  | "requisitions"
  | "quotations"
  | "orders"
  | "deliveries"
  | "receipts"
  | "collections"
  | "payments"
  | "returns"
  | "comparisons"
  | "approvals"
  | "budgets"
  | "additionalCosts"
  | "priceLists"
  | "invoices"
  | "configuration"
  | "reports";
type Line = {
  articleId: string;
  unitId: string;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  taxPercent: number;
  sourceLineId: string;
  lotNumber: string;
  serialNumber: string;
};
const k = (v: string) => v as TranslationKey;
const entities: Record<string, string> = {
  customers: "customers",
  suppliers: "suppliers",
  supplierContacts: "supplier-contacts",
  supplierAddresses: "supplier-addresses",
  supplierArticles: "supplier-articles",
  requisitions: "requisitions",
  quotations: "quotations",
  orders: "orders",
  deliveries: "deliveries",
  receipts: "receipts",
  collections: "receipts",
  payments: "payments",
  returns: "returns",
  approvals: "approvals",
  budgets: "budgets",
  additionalCosts: "additional-costs",
  priceLists: "price-lists",
  invoices: "invoices",
  configuration: "counters",
};
const newLine = (): Line => ({
  articleId: "",
  unitId: "",
  quantity: 1,
  unitPrice: 0,
  discountPercent: 0,
  taxPercent: 21,
  sourceLineId: "",
  lotNumber: "",
  serialNumber: "",
});

export function CommercialPage({
  module,
  section = "overview",
}: {
  module: CommercialModule;
  section?: CommercialSection;
}) {
  const { user } = useAuth(),
    { t, formatNumber } = useLocale(),
    [data, setData] = useState<CommercialSnapshot | null>(null),
    [error, setError] = useState<string>(),
    [editing, setEditing] = useState<CommercialRecord | null | undefined>();
  const load = async () => {
    try {
      setData(await getCommercial(module));
      setError(undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : "commercial.loadError");
    }
  };
  useEffect(() => {
    void load();
  }, [module, user?.activeCompanyId]);
  const sourceRows = (() => {
    const source =
      data?.[
        section === "supplierArticles"
          ? "supplierArticles"
          : section === "configuration"
            ? "counters"
            : section
      ];
    return (Array.isArray(source) ? source : []) as CommercialRecord[];
  })();
  const list = useClientRecordList(sourceRows, `${module}:${section}`);
  if (!data)
    return (
      <PageLoading
        label={t(k(`${module}.loading`))}
        error={error ? t(k(error)) : null}
      />
    );
  if (module === "sales" && section === "configuration")
    return <SalesFiscalConfigurationPage data={data} reload={load} />;
  if (module === "purchasing" && section === "configuration")
    return <PurchasingConfigurationPage data={data} reload={load} />;
  if (module === "sales" && section === "customers")
    return <SalesCustomersPage data={data} reload={load} />;
  if (
    module === "sales" &&
    (section === "customerContacts" || section === "customerAddresses")
  )
    return (
      <SalesCustomerStructurePage section={section} data={data} reload={load} />
    );
  if (module === "sales" && section === "collections")
    return <SalesCollectionsPage data={data} reload={load} />;
  if (
    module === "purchasing" &&
    (section === "supplierContacts" || section === "supplierAddresses")
  )
    return (
      <PurchasingSupplierStructurePage
        section={section}
        data={data}
        reload={load}
      />
    );
  if (module === "purchasing" && section === "payments")
    return <PurchasingPaymentsPage data={data} reload={load} />;
  if (
    module === "purchasing" &&
    ["comparisons", "approvals", "budgets", "additionalCosts"].includes(section)
  )
    return (
      <PurchasingControlPage
        section={
          section as "comparisons" | "approvals" | "budgets" | "additionalCosts"
        }
        data={data}
        reload={load}
      />
    );
  if (module === "sales" && section === "priceLists")
    return <SalesPriceListsPage data={data} reload={load} />;
  if (module === "sales" && section === "reports")
    return <SalesReportsPage data={data} />;
  if (module === "purchasing" && section === "reports")
    return <PurchasingReportsPage data={data} />;
  if (editing !== undefined)
  {
    const recordIndex = editing ? list.rows.findIndex((record) => record.id === editing.id) : -1;
    return (
      <CommercialRecordPage
        module={module}
        section={section}
        item={editing}
        data={data}
        onCancel={() => setEditing(undefined)}
        onSaved={async () => {
          setEditing(undefined);
          await load();
        }}
        onPrevious={recordIndex > 0 ? () => setEditing(list.rows[recordIndex - 1]) : null}
        onNext={recordIndex >= 0 && recordIndex < list.rows.length - 1 ? () => setEditing(list.rows[recordIndex + 1]) : null}
      />
    );
  }
  return (
    <main className="page-content">
      <Header module={module} company={data.companyName} />
      {section === "overview" ? (
        <>
          <section className="maintenance-kpis quality-kpis">
            {Object.entries(data.overview).map(([name, value]) => (
              <article
                className="indicator-card neutral maintenance-kpi"
                key={name}
              >
                <span className="indicator-label">
                  {t(k(`${module}.kpi.${name}`))}
                </span>
                <div className="indicator-value">{formatNumber(value)}</div>
              </article>
            ))}
          </section>
          <section className="capacity-overview-grid">
            {sections(module).map((s) => (
              <Link
                className="capacity-overview-card"
                key={s}
                to={`/app/${module === "sales" ? "ventas" : "compras"}/${route(s)}`}
              >
                <h2>{t(k(`${module}.${s}`))}</h2>
                <p>{t(k(`${module}.${s}Description`))}</p>
                <span className="capacity-overview-link">
                  {t("capacity.openSection")} →
                </span>
              </Link>
            ))}
          </section>
        </>
      ) : section === "reports" ? (
        <section className="empty-state-card">
          <span className="empty-state-icon">📊</span>
          <h2>{t("menu.reports")}</h2>
          <p>{t(k(`${module}.reportsDescription`))}</p>
        </section>
      ) : (
        <section className="data-panel administration-panel">
          <div className="section-heading">
            <h2>{t(k(`${module}.${section}`))}</h2>
            {user?.permissions[module] === "Write" && (
              <button
                className="primary-button"
                onClick={() => setEditing(null)}
              >
                + {t("administration.add")}
              </button>
            )}
          </div>
          <ClientRecordListControls list={list} />
          <div className="administration-table-wrap">
            <table className="data-table administration-table">
              <thead>
                <tr>
                  <SortableHeader
                    label={t("aplPanel.code")}
                    active={list.sort === "code"}
                    direction={list.direction}
                    onClick={() => list.changeSort("code")}
                  />
                  <SortableHeader
                    label={t("aplPanel.name")}
                    active={list.sort === "name"}
                    direction={list.direction}
                    onClick={() => list.changeSort("name")}
                  />
                  <SortableHeader
                    label={t("planning.status")}
                    active={list.sort === "status"}
                    direction={list.direction}
                    onClick={() => list.changeSort("status")}
                  />
                  <SortableHeader
                    label={t(k("commercial.total"))}
                    active={list.sort === "totalAmount"}
                    direction={list.direction}
                    onClick={() => list.changeSort("totalAmount")}
                  />
                </tr>
              </thead>
              <tbody>
                {list.rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <button
                        className="record-code-link"
                        onClick={() => setEditing(r)}
                      >
                        {String(r.code ?? r.number ?? "—")}
                      </button>
                    </td>
                    <td>
                      {String(
                        r.name ??
                          r.customerReference ??
                          r.supplierReference ??
                          "—",
                      )}
                    </td>
                    <td>
                      {String(
                        r.status ??
                          (r.isActive === false
                            ? t("administration.inactive")
                            : t("administration.active")),
                      )}
                    </td>
                    <td>
                      {typeof r.totalAmount === "number"
                        ? formatNumber(r.totalAmount)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!list.rows.length && (
            <p className="empty-state">{t(k("commercial.noResults"))}</p>
          )}
        </section>
      )}
    </main>
  );
}

function Header({
  module,
  company,
}: {
  module: CommercialModule;
  company: string;
}) {
  const { t } = useLocale();
  return (
    <header className="welcome administration-heading">
      <div>
        <span className="eyebrow">{t(k(`${module}.eyebrow`))}</span>
        <h1>{t(k(`${module}.title`))}</h1>
        <p>
          {company} · {t(k(`${module}.subtitle`))}
        </p>
      </div>
    </header>
  );
}

function Editor({
  module,
  section,
  item,
  data,
  cancel,
  saved,
}: {
  module: CommercialModule;
  section: CommercialSection;
  item: CommercialRecord | null;
  data: CommercialSnapshot;
  cancel: () => void;
  saved: () => void;
}) {
  const { t } = useLocale(),
    [lines, setLines] = useState<Line[]>([newLine()]),
    [error, setError] = useState<string>();
  const master = ["customers", "suppliers"].includes(section),
    supplierArticle = section === "supplierArticles",
    counter = section === "configuration",
    document = !master && !supplierArticle && !counter;
  const party = (data[module === "sales" ? "customers" : "suppliers"] ??
    []) as CommercialRecord[];
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(e.currentTarget),
    );
    for (const name of [
      "creditLimit",
      "leadTimeDays",
      "unitPrice",
      "minimumQuantity",
      "nextNumber",
      "padding",
    ])
      if (body[name]) body[name] = Number(body[name]);
    if (document)
      body.lines = lines.map((x) => ({
        ...x,
        unitId: x.unitId || null,
        sourceLineId: x.sourceLineId || null,
        requiredAtUtc: body.expectedAtUtc ?? body.requestedAtUtc,
      }));
    try {
      await saveCommercial(module, entities[section], item?.id, body);
      await saved();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "commercial.saveError",
      );
    }
  };
  const change = (i: number, name: keyof Line, value: string | number) =>
    setLines((v) => v.map((x, n) => (n === i ? { ...x, [name]: value } : x)));
  return (
    <main className="page-content">
      <AppModal title={t(k(`${module}.${section}`))} closeLabel={t("recordNavigation.backToList")} onClose={cancel} saveForm wide feedback={error ? t(k(error)) : null}>
      <section className="data-panel administration-panel">
        <form
          id="commercial-form"
          className="administration-form"
          onSubmit={submit}
        >
          <label>
            <span>
              {t(
                master || supplierArticle ? "aplPanel.code" : "quality.number",
              )}
            </span>
            <input
              name={
                master ? "code" : supplierArticle ? "supplierCode" : "number"
              }
              defaultValue={String(item?.code ?? item?.number ?? "")}
              required
            />
          </label>
          {master && (
            <>
              <label>
                <span>{t("aplPanel.name")}</span>
                <input
                  name="name"
                  defaultValue={String(item?.name ?? "")}
                  required
                />
              </label>
              <label>
                <span>{t(k("commercial.currency"))}</span>
                <input
                  name="currencyCode"
                  defaultValue="EUR"
                  maxLength={3}
                  required
                />
              </label>
            </>
          )}
          {supplierArticle && (
            <>
              <Select
                label={t(k("purchasing.supplier"))}
                name="supplierId"
                records={data.suppliers as CommercialRecord[]}
              />
              <label>
                <span>{t(k("commercial.article"))}</span>
                <select name="articleId" required>
                  {data.articles.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.code} · {x.name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
          {document && (
            <>
              <Select
                label={t(
                  module === "sales"
                    ? k("sales.customer")
                    : k("purchasing.supplier"),
                )}
                name={module === "sales" ? "customerId" : "supplierId"}
                records={party}
              />
              <label>
                <span>{t(k("commercial.documentDate"))}</span>
                <input name="documentDate" type="date" required />
              </label>
              {section === "quotations" && (
                <label>
                  <span>{t(k("commercial.validUntil"))}</span>
                  <input name="validUntil" type="date" required />
                </label>
              )}
              {section === "orders" && (
                <label>
                  <span>{t(k("commercial.expectedAt"))}</span>
                  <input
                    name={
                      module === "sales" ? "requestedAtUtc" : "expectedAtUtc"
                    }
                    type="datetime-local"
                    required
                  />
                </label>
              )}
              {["deliveries", "receipts"].includes(section) && (
                <>
                  <Select
                    label={t(k("commercial.order"))}
                    name={
                      module === "sales" ? "salesOrderId" : "purchaseOrderId"
                    }
                    records={data.orders as CommercialRecord[]}
                  />
                  <label>
                    <span>{t(k("commercial.warehouse"))}</span>
                    <select name="warehouseId" required>
                      {data.warehouses.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.code} · {x.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}
            </>
          )}
          {counter && (
            <>
              <label>
                <span>{t(k("commercial.module"))}</span>
                <input name="module" required />
              </label>
              <label>
                <span>{t(k("commercial.documentType"))}</span>
                <input name="documentType" required />
              </label>
              <label>
                <span>{t(k("commercial.series"))}</span>
                <input name="series" required />
              </label>
              <label>
                <span>{t(k("commercial.nextNumber"))}</span>
                <input name="nextNumber" type="number" min="1" required />
              </label>
            </>
          )}
          <label className="form-field-wide">
            <span>{t("administration.description")}</span>
            <textarea
              name={
                master
                  ? module === "sales"
                    ? "billingAddress"
                    : "address"
                  : "notes"
              }
            />
          </label>
        </form>
        {document && (
          <div>
            <div className="section-heading">
              <h2>{t(k("commercial.lines"))}</h2>
              <button
                className="secondary-button"
                type="button"
                onClick={() => setLines((v) => [...v, newLine()])}
              >
                + {t("administration.add")}
              </button>
            </div>
            {lines.map((line, i) => (
              <div className="administration-form" key={i}>
                <label>
                  <span>{t(k("commercial.article"))}</span>
                  <select
                    value={line.articleId}
                    onChange={(e) => change(i, "articleId", e.target.value)}
                    required
                  >
                    <option value="">—</option>
                    {data.articles.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.code} · {x.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>{t("quality.quantity")}</span>
                  <input
                    type="number"
                    min="0.000001"
                    step="any"
                    value={line.quantity}
                    onChange={(e) =>
                      change(i, "quantity", Number(e.target.value))
                    }
                  />
                </label>
                <label>
                  <span>{t(k("commercial.unitPrice"))}</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={line.unitPrice}
                    onChange={(e) =>
                      change(i, "unitPrice", Number(e.target.value))
                    }
                  />
                </label>
                <button
                  className="danger-button"
                  type="button"
                  onClick={() => setLines((v) => v.filter((_, n) => n !== i))}
                  aria-label={t(k("commercial.removeLine"))}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
      </AppModal>
    </main>
  );
}
function Select({
  label,
  name,
  records,
}: {
  label: string;
  name: string;
  records: CommercialRecord[];
}) {
  return (
    <label>
      <span>{label}</span>
      <select name={name} required>
        <option value="">—</option>
        {records.map((x) => (
          <option key={x.id} value={x.id}>
            {String(x.code ?? x.number)} · {String(x.name ?? "")}
          </option>
        ))}
      </select>
    </label>
  );
}
void Editor;
function sections(module: CommercialModule): CommercialSection[] {
  return module === "sales"
    ? [
        "customers",
        "customerContacts",
        "customerAddresses",
        "quotations",
        "orders",
        "deliveries",
        "invoices",
        "collections",
        "priceLists",
        "configuration",
        "reports",
      ]
    : [
        "suppliers",
        "supplierContacts",
        "supplierAddresses",
        "supplierArticles",
        "requisitions",
        "quotations",
        "orders",
        "receipts",
        "invoices",
        "payments",
        "returns",
        "comparisons",
        "approvals",
        "budgets",
        "additionalCosts",
        "configuration",
        "reports",
      ];
}
function route(s: CommercialSection) {
  return (
    {
      supplierArticles: "articulos-proveedor",
      supplierContacts: "contactos-proveedor",
      supplierAddresses: "direcciones-proveedor",
      requisitions: "solicitudes",
      customers: "clientes",
      customerContacts: "contactos",
      customerAddresses: "direcciones",
      suppliers: "proveedores",
      quotations: "ofertas",
      orders: "pedidos",
      deliveries: "albaranes",
      receipts: "albaranes",
      collections: "cobros",
      payments: "pagos",
      returns: "devoluciones",
      comparisons: "comparativa",
      approvals: "aprobaciones",
      budgets: "presupuestos",
      additionalCosts: "costes-adicionales",
      priceLists: "tarifas",
      invoices: "facturas",
      configuration: "configuracion",
      reports: "informes",
    } as Record<string, string>
  )[s];
}

function PurchasingSupplierStructurePage({
  section,
  data,
  reload,
}: {
  section: "supplierContacts" | "supplierAddresses";
  data: CommercialSnapshot;
  reload: () => Promise<void>;
}) {
  const { t } = useLocale();
  const [error, setError] = useState<string>();
  const suppliers = (data.suppliers ?? []) as CommercialRecord[];
  const records = (data[section] ?? []) as CommercialRecord[];
  const list = useClientRecordList(records, `purchasing:${section}`);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(form),
    );
    body.isDefault =
      (form.elements.namedItem("isDefault") as HTMLInputElement | null)
        ?.checked ?? false;
    try {
      await saveCommercial(
        "purchasing",
        section === "supplierContacts"
          ? "supplier-contacts"
          : "supplier-addresses",
        undefined,
        body,
      );
      form.reset();
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "commercial.saveError",
      );
    }
  };
  return (
    <main className="page-content">
      <Header module="purchasing" company={data.companyName} />
      {error && (
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      )}
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t(k(`purchasing.${section}`))}</h2>
        </div>
        <form className="administration-form" onSubmit={submit}>
          <label>
            <span>{t("purchasing.suppliers")}</span>
            <select name="parentId" required>
              <option value="">—</option>
              {suppliers.map((x) => (
                <option key={x.id} value={x.id}>
                  {String(x.code)} · {String(x.name)}
                </option>
              ))}
            </select>
          </label>
          {section === "supplierContacts" ? (
            <>
              <label>
                <span>{t("aplPanel.name")}</span>
                <input name="name" required />
              </label>
              <label>
                <span>{t("sales.position")}</span>
                <input name="position" />
              </label>
              <label>
                <span>{t("sales.email")}</span>
                <input name="email" type="email" />
              </label>
              <label>
                <span>{t("sales.phone")}</span>
                <input name="phone" />
              </label>
            </>
          ) : (
            <>
              <label>
                <span>{t("aplPanel.code")}</span>
                <input name="code" required />
              </label>
              <label>
                <span>{t("purchasing.addressKind")}</span>
                <select name="addressKind">
                  <option value="Fiscal">{t("purchasing.fiscal")}</option>
                  <option value="Ordering">{t("purchasing.ordering")}</option>
                  <option value="Returns">{t("purchasing.returns")}</option>
                  <option value="Other">{t("sales.other")}</option>
                </select>
              </label>
              <label>
                <span>{t("aplPanel.name")}</span>
                <input name="name" required />
              </label>
              <label className="form-field-wide">
                <span>{t("sales.addressLine")}</span>
                <input name="addressLine" required />
              </label>
              <label>
                <span>{t("sales.postalCode")}</span>
                <input name="postalCode" />
              </label>
              <label>
                <span>{t("sales.city")}</span>
                <input name="city" />
              </label>
              <label>
                <span>{t("sales.province")}</span>
                <input name="province" />
              </label>
              <label>
                <span>{t("sales.countryCode")}</span>
                <input
                  name="countryCode"
                  defaultValue="ES"
                  maxLength={2}
                  required
                />
              </label>
            </>
          )}
          <label className="checkbox-label">
            <input type="checkbox" name="isDefault" />
            <span>{t("sales.primaryOrDefault")}</span>
          </label>
          <button className="primary-button" type="submit">
            {t("administration.add")}
          </button>
        </form>
        <ClientRecordListControls list={list} />
        <div className="administration-table-wrap">
          <table className="data-table administration-table">
            <thead>
              <tr>
                <th>{t("purchasing.suppliers")}</th>
                <th>{t("aplPanel.name")}</th>
                <th>
                  {t(
                    section === "supplierContacts"
                      ? "sales.email"
                      : "sales.city",
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {list.rows.map((x) => (
                <tr key={x.id}>
                  <td>
                    {String(
                      suppliers.find((s) => s.id === x.supplierId)?.code ?? "—",
                    )}
                  </td>
                  <td>{String(x.name)}</td>
                  <td>
                    {String(
                      section === "supplierContacts"
                        ? (x.email ?? "—")
                        : (x.city ?? "—"),
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function PurchasingPaymentsPage({
  data,
  reload,
}: {
  data: CommercialSnapshot;
  reload: () => Promise<void>;
}) {
  const { t, formatNumber } = useLocale();
  const [error, setError] = useState<string>();
  const dueDates = (data.invoiceDueDates ?? []) as CommercialRecord[];
  const invoices = (data.invoices ?? []) as CommercialRecord[];
  const payments = (data.payments ?? []) as CommercialRecord[];
  const list = useClientRecordList(payments, "purchasing:payments");
  const pending = dueDates.filter(
    (x) => Number(x.paidAmount ?? 0) < Number(x.amount ?? 0),
  );
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(form),
    );
    const due = dueDates.find((x) => x.id === body.dueDateId);
    if (!due) return;
    body.invoiceId = due.purchaseInvoiceId;
    body.amount = Number(body.amount);
    try {
      await saveCommercial("purchasing", "payments", undefined, body);
      form.reset();
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "commercial.saveError",
      );
    }
  };
  return (
    <main className="page-content">
      <Header module="purchasing" company={data.companyName} />
      {error && (
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      )}
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t("purchasing.payments")}</h2>
        </div>
        <form className="administration-form" onSubmit={submit}>
          <label>
            <span>{t("purchasing.dueDate")}</span>
            <select name="dueDateId" required>
              <option value="">—</option>
              {pending.map((x) => (
                <option key={x.id} value={x.id}>
                  {String(
                    invoices.find((i) => i.id === x.purchaseInvoiceId)
                      ?.number ?? "—",
                  )}{" "}
                  · {String(x.dueDate)} ·{" "}
                  {formatNumber(Number(x.amount) - Number(x.paidAmount ?? 0))}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t("quality.number")}</span>
            <input name="number" required />
          </label>
          <label>
            <span>{t("purchasing.paidOn")}</span>
            <input name="paidOn" type="date" required />
          </label>
          <label>
            <span>{t("finance.amount")}</span>
            <input
              name="amount"
              type="number"
              min="0.01"
              step="0.01"
              required
            />
          </label>
          <label>
            <span>{t("purchasing.paymentMethod")}</span>
            <input name="paymentMethod" required />
          </label>
          <label>
            <span>{t("purchasing.reference")}</span>
            <input name="reference" />
          </label>
          <button className="primary-button" type="submit">
            {t("administration.add")}
          </button>
        </form>
      </section>
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t("purchasing.payments")}</h2>
        </div>
        <ClientRecordListControls list={list} />
        <div className="administration-table-wrap">
          <table className="data-table administration-table">
            <thead>
              <tr>
                <th>{t("quality.number")}</th>
                <th>{t("purchasing.paidOn")}</th>
                <th>{t("finance.amount")}</th>
                <th>{t("planning.status")}</th>
              </tr>
            </thead>
            <tbody>
              {list.rows.map((x) => (
                <tr key={x.id}>
                  <td>{String(x.number)}</td>
                  <td>{String(x.paidOn)}</td>
                  <td>{formatNumber(Number(x.amount))}</td>
                  <td>{String(x.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function PurchasingControlPage({
  section,
  data,
  reload,
}: {
  section: "comparisons" | "approvals" | "budgets" | "additionalCosts";
  data: CommercialSnapshot;
  reload: () => Promise<void>;
}) {
  const { t, formatNumber } = useLocale();
  const [error, setError] = useState<string>();
  const award = async (id: string) => {
    try {
      await changeCommercialStatus("purchasing", "quotations", id, "Accepted");
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "commercial.saveError",
      );
    }
  };
  const submit = async (entity: string, event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(form),
    );
    for (const key of [
      "minimumAmount",
      "maximumAmount",
      "requiredApprovals",
      "amount",
    ])
      if (body[key] !== undefined && body[key] !== "")
        body[key] = Number(body[key]);
    body.isActive = true;
    try {
      await saveCommercial("purchasing", entity, undefined, body);
      form.reset();
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "commercial.saveError",
      );
    }
  };
  const comparisons = (data.quotationComparisons ?? []) as CommercialRecord[],
    requisitions = (data.requisitions ?? []) as CommercialRecord[],
    quotations = (data.quotations ?? []) as CommercialRecord[],
    suppliers = (data.suppliers ?? []) as CommercialRecord[];
  const approvals = (data.approvals ?? []) as CommercialRecord[],
    orders = (data.orders ?? []) as CommercialRecord[],
    budgets = (data.budgets ?? []) as CommercialRecord[],
    usages = (data.budgetUsages ?? []) as CommercialRecord[],
    costs = (data.additionalCosts ?? []) as CommercialRecord[],
    receipts = (data.receipts ?? []) as CommercialRecord[];
  const comparisonList = useClientRecordList(
    comparisons,
    "purchasing:comparisons",
  );
  const approvalList = useClientRecordList(approvals, "purchasing:approvals");
  const budgetList = useClientRecordList(budgets, "purchasing:budgets");
  const costList = useClientRecordList(costs, "purchasing:additionalCosts");
  return (
    <main className="page-content">
      <Header module="purchasing" company={data.companyName} />
      {error && (
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      )}
      {section === "comparisons" && (
        <section className="data-panel administration-panel">
          <div className="section-heading">
            <h2>{t("purchasing.comparisons")}</h2>
          </div>
          <ClientRecordListControls list={comparisonList} />
          <div className="administration-table-wrap">
            <table className="data-table administration-table">
              <thead>
                <tr>
                  <th>{t("purchasing.requisitions")}</th>
                  <th>{t("purchasing.suppliers")}</th>
                  <th>{t("commercial.total")}</th>
                  <th>{t("purchasing.leadTimeDays")}</th>
                  <th>{t("commercial.validUntil")}</th>
                  <th>
                    <span className="sr-only">{t("common.actions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonList.rows.map((x) => {
                  const quotation = quotations.find(
                    (q) => q.id === x.supplierQuotationId,
                  );
                  return (
                    <tr key={x.supplierQuotationId as string}>
                      <td>
                        {String(
                          requisitions.find(
                            (r) => r.id === x.purchaseRequisitionId,
                          )?.number ?? "—",
                        )}
                      </td>
                      <td>
                        {String(
                          suppliers.find((s) => s.id === quotation?.supplierId)
                            ?.name ?? "—",
                        )}
                      </td>
                      <td>{formatNumber(Number(x.total))}</td>
                      <td>{String(x.maximumLeadTimeDays)}</td>
                      <td>{String(x.validUntil)}</td>
                      <td>
                        {quotation?.status === "Received" && (
                          <button
                            className="secondary-button"
                            type="button"
                            onClick={() =>
                              void award(String(x.supplierQuotationId))
                            }
                          >
                            {t("purchasing.award")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {section === "approvals" && (
        <>
          <section className="data-panel administration-panel">
            <div className="section-heading">
              <h2>{t("purchasing.approvalPolicies")}</h2>
            </div>
            <form
              className="administration-form"
              onSubmit={(e) => void submit("approval-policies", e)}
            >
              <label>
                <span>{t("aplPanel.code")}</span>
                <input name="code" required />
              </label>
              <label>
                <span>{t("aplPanel.name")}</span>
                <input name="name" required />
              </label>
              <label>
                <span>{t("purchasing.document")}</span>
                <select name="approvalDocumentType">
                  <option value="Requisition">
                    {t("purchasing.requisitions")}
                  </option>
                  <option value="Order">{t("purchasing.orders")}</option>
                </select>
              </label>
              <label>
                <span>{t("purchasing.minimumAmount")}</span>
                <input
                  name="minimumAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue="0"
                />
              </label>
              <label>
                <span>{t("purchasing.maximumAmount")}</span>
                <input name="maximumAmount" type="number" min="0" step="0.01" />
              </label>
              <label>
                <span>{t("purchasing.requiredApprovals")}</span>
                <input
                  name="requiredApprovals"
                  type="number"
                  min="1"
                  max="20"
                  defaultValue="1"
                />
              </label>
              <button className="primary-button" type="submit">
                {t("administration.add")}
              </button>
            </form>
          </section>
          <section className="data-panel administration-panel">
            <div className="section-heading">
              <h2>{t("purchasing.recordDecision")}</h2>
            </div>
            <form
              className="administration-form"
              onSubmit={(e) => void submit("approvals", e)}
            >
              <label>
                <span>{t("purchasing.document")}</span>
                <select name="approvalDocumentType">
                  <option value="Order">{t("purchasing.orders")}</option>
                  <option value="Requisition">
                    {t("purchasing.requisitions")}
                  </option>
                </select>
              </label>
              <label>
                <span>{t("purchasing.document")}</span>
                <select name="documentId" required>
                  <option value="">—</option>
                  {orders.map((x) => (
                    <option key={x.id} value={x.id}>
                      {String(x.number)}
                    </option>
                  ))}
                  {requisitions.map((x) => (
                    <option key={x.id} value={x.id}>
                      {String(x.number)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>{t("planning.status")}</span>
                <select name="approvalStatus">
                  <option value="Approved">{t("purchasing.approved")}</option>
                  <option value="Rejected">{t("purchasing.rejected")}</option>
                </select>
              </label>
              <label className="form-field-wide">
                <span>{t("purchasing.comments")}</span>
                <textarea name="comments" />
              </label>
              <button className="primary-button" type="submit">
                {t("administration.save")}
              </button>
            </form>
            <ClientRecordListControls list={approvalList} />
            <div className="administration-table-wrap">
              <table className="data-table administration-table">
                <thead>
                  <tr>
                    <th>{t("purchasing.document")}</th>
                    <th>{t("planning.status")}</th>
                    <th>{t("purchasing.comments")}</th>
                  </tr>
                </thead>
                <tbody>
                  {approvalList.rows.map((x) => (
                    <tr key={x.id}>
                      <td>{String(x.documentId)}</td>
                      <td>{String(x.status)}</td>
                      <td>{String(x.comments ?? "")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
      {section === "budgets" && (
        <section className="data-panel administration-panel">
          <div className="section-heading">
            <h2>{t("purchasing.budgets")}</h2>
          </div>
          <form
            className="administration-form"
            onSubmit={(e) => void submit("budgets", e)}
          >
            <label>
              <span>{t("aplPanel.code")}</span>
              <input name="code" required />
            </label>
            <label>
              <span>{t("aplPanel.name")}</span>
              <input name="name" required />
            </label>
            <label>
              <span>{t("purchasing.costCenter")}</span>
              <input name="costCenter" />
            </label>
            <label>
              <span>{t("finance.startsOn")}</span>
              <input name="startsOn" type="date" required />
            </label>
            <label>
              <span>{t("finance.endsOn")}</span>
              <input name="endsOn" type="date" required />
            </label>
            <label>
              <span>{t("finance.amount")}</span>
              <input name="amount" type="number" min="0" step="0.01" required />
            </label>
            <button className="primary-button" type="submit">
              {t("administration.add")}
            </button>
          </form>
          <ClientRecordListControls list={budgetList} />
          <div className="administration-table-wrap">
            <table className="data-table administration-table">
              <thead>
                <tr>
                  <th>{t("aplPanel.code")}</th>
                  <th>{t("finance.amount")}</th>
                  <th>{t("purchasing.committed")}</th>
                  <th>{t("purchasing.actual")}</th>
                  <th>{t("purchasing.available")}</th>
                </tr>
              </thead>
              <tbody>
                {budgetList.rows.map((x) => {
                  const u = usages.find((y) => y.budgetId === x.id);
                  return (
                    <tr key={x.id}>
                      <td>{String(x.code)}</td>
                      <td>{formatNumber(Number(x.amount))}</td>
                      <td>{formatNumber(Number(u?.committed ?? 0))}</td>
                      <td>{formatNumber(Number(u?.actual ?? 0))}</td>
                      <td>{formatNumber(Number(u?.available ?? 0))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {section === "additionalCosts" && (
        <section className="data-panel administration-panel">
          <div className="section-heading">
            <h2>{t("purchasing.additionalCosts")}</h2>
          </div>
          <form
            className="administration-form"
            onSubmit={(e) => void submit("additional-costs", e)}
          >
            <label>
              <span>{t("quality.number")}</span>
              <input name="number" required />
            </label>
            <label>
              <span>{t("purchasing.receipts")}</span>
              <select name="purchaseReceiptId" required>
                <option value="">—</option>
                {receipts.map((x) => (
                  <option key={x.id} value={x.id}>
                    {String(x.number)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{t("administration.description")}</span>
              <input name="name" required />
            </label>
            <label>
              <span>{t("finance.amount")}</span>
              <input
                name="amount"
                type="number"
                min="0.01"
                step="0.01"
                required
              />
            </label>
            <label>
              <span>{t("purchasing.allocationMethod")}</span>
              <select name="allocationMethod">
                <option value="Quantity">{t("quality.quantity")}</option>
                <option value="NetValue">{t("purchasing.netValue")}</option>
              </select>
            </label>
            <button className="primary-button" type="submit">
              {t("administration.add")}
            </button>
          </form>
          <ClientRecordListControls list={costList} />
          <div className="administration-table-wrap">
            <table className="data-table administration-table">
              <thead>
                <tr>
                  <th>{t("quality.number")}</th>
                  <th>{t("administration.description")}</th>
                  <th>{t("finance.amount")}</th>
                </tr>
              </thead>
              <tbody>
                {costList.rows.map((x) => (
                  <tr key={x.id}>
                    <td>{String(x.number)}</td>
                    <td>{String(x.description)}</td>
                    <td>{formatNumber(Number(x.amount))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </main>
  );
}

function SalesCustomerStructurePage({
  section,
  data,
  reload,
}: {
  section: "customerContacts" | "customerAddresses";
  data: CommercialSnapshot;
  reload: () => Promise<void>;
}) {
  const { t } = useLocale();
  const [error, setError] = useState<string>();
  const customers = (data.customers ?? []) as CommercialRecord[];
  const records = (data[section] ?? []) as CommercialRecord[];
  const list = useClientRecordList(records, `sales:${section}`);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(e.currentTarget),
    );
    body.isDefault =
      (
        e.currentTarget.elements.namedItem(
          "isDefault",
        ) as HTMLInputElement | null
      )?.checked ?? false;
    try {
      await saveCommercial(
        "sales",
        section === "customerContacts"
          ? "customer-contacts"
          : "customer-addresses",
        undefined,
        body,
      );
      e.currentTarget.reset();
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "commercial.saveError",
      );
    }
  };
  return (
    <main className="page-content">
      <Header module="sales" company={data.companyName} />
      {error && (
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      )}
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t(k(`sales.${section}`))}</h2>
        </div>
        <form className="administration-form" onSubmit={submit}>
          <label>
            <span>{t("sales.customers")}</span>
            <select name="parentId" required>
              <option value="">—</option>
              {customers.map((x) => (
                <option key={x.id} value={x.id}>
                  {String(x.code)} · {String(x.name)}
                </option>
              ))}
            </select>
          </label>
          {section === "customerContacts" ? (
            <>
              <label>
                <span>{t("aplPanel.name")}</span>
                <input name="name" required />
              </label>
              <label>
                <span>{t(k("sales.position"))}</span>
                <input name="position" />
              </label>
              <label>
                <span>{t(k("sales.email"))}</span>
                <input name="email" type="email" required />
              </label>
              <label>
                <span>{t(k("sales.phone"))}</span>
                <input name="phone" />
              </label>
            </>
          ) : (
            <>
              <label>
                <span>{t("aplPanel.code")}</span>
                <input name="code" required />
              </label>
              <label>
                <span>{t(k("sales.addressKind"))}</span>
                <select name="addressKind">
                  <option value="Billing">{t(k("sales.billing"))}</option>
                  <option value="Shipping">{t(k("sales.shipping"))}</option>
                  <option value="Other">{t(k("sales.other"))}</option>
                </select>
              </label>
              <label>
                <span>{t("aplPanel.name")}</span>
                <input name="name" required />
              </label>
              <label className="form-field-wide">
                <span>{t(k("sales.addressLine"))}</span>
                <input name="addressLine" required />
              </label>
              <label>
                <span>{t(k("sales.postalCode"))}</span>
                <input name="postalCode" />
              </label>
              <label>
                <span>{t(k("sales.city"))}</span>
                <input name="city" />
              </label>
              <label>
                <span>{t(k("sales.province"))}</span>
                <input name="province" />
              </label>
              <label>
                <span>{t(k("sales.countryCode"))}</span>
                <input
                  name="countryCode"
                  defaultValue="ES"
                  maxLength={2}
                  required
                />
              </label>
            </>
          )}
          <label className="checkbox-label">
            <input type="checkbox" name="isDefault" />
            <span>{t(k("sales.primaryOrDefault"))}</span>
          </label>
          <button className="primary-button" type="submit">
            {t("administration.add")}
          </button>
        </form>
        <ClientRecordListControls list={list} />
        <div className="administration-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("sales.customers")}</th>
                <th>{t("aplPanel.name")}</th>
                <th>
                  {t(
                    k(
                      section === "customerContacts"
                        ? "sales.email"
                        : "sales.city",
                    ),
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {list.rows.map((x) => (
                <tr key={x.id}>
                  <td>
                    {String(
                      customers.find((c) => c.id === x.customerId)?.code ?? "—",
                    )}
                  </td>
                  <td>{String(x.name)}</td>
                  <td>
                    {String(section === "customerContacts" ? x.email : x.city)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function SalesCollectionsPage({
  data,
  reload,
}: {
  data: CommercialSnapshot;
  reload: () => Promise<void>;
}) {
  const { t, formatNumber } = useLocale();
  const [error, setError] = useState<string>();
  const [creating, setCreating] = useState(false);
  const dueDates = (data.invoiceDueDates ?? []) as CommercialRecord[];
  const invoices = (data.invoices ?? []) as CommercialRecord[];
  const receipts = (data.receipts ?? []) as CommercialRecord[];
  const list = useClientRecordList(receipts, "sales:collections");
  const pending = dueDates.filter(
    (x) => Number(x.paidAmount ?? 0) < Number(x.amount ?? 0),
  );
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(e.currentTarget),
    );
    const due = dueDates.find((x) => x.id === body.dueDateId);
    if (!due) return;
    body.invoiceId = due.salesInvoiceId;
    body.amount = Number(body.amount);
    try {
      await saveCommercial("sales", "receipts", undefined, body);
      e.currentTarget.reset();
      setCreating(false);
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "commercial.saveError",
      );
    }
  };
  return (
    <main className="page-content commercial-management-page">
      <Header module="sales" company={data.companyName} />
      {error && (
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      )}
      {creating && <AppModal title={t(k("sales.collections"))} closeLabel={t("recordNavigation.backToList")} onClose={() => setCreating(false)} saveForm wide feedback={error ? t(k(error)) : null}>
        <form className="administration-form record-form" onSubmit={submit}>
          <div className="compact-form-grid">
          <label>
            <span>{t(k("sales.dueDate"))}</span>
            <select name="dueDateId" required>
              <option value="">—</option>
              {pending.map((x) => (
                <option key={x.id} value={x.id}>
                  {String(
                    invoices.find((i) => i.id === x.salesInvoiceId)?.number ??
                      "—",
                  )}{" "}
                  · {String(x.dueDate)} ·{" "}
                  {formatNumber(Number(x.amount) - Number(x.paidAmount ?? 0))}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t("quality.number")}</span>
            <input name="number" required />
          </label>
          <label>
            <span>{t(k("sales.receivedOn"))}</span>
            <input name="receivedOn" type="date" required />
          </label>
          <label>
            <span>{t(k("finance.amount"))}</span>
            <input
              name="amount"
              type="number"
              min="0.01"
              step="0.01"
              required
            />
          </label>
          <label>
            <span>{t(k("sales.paymentMethod"))}</span>
            <input name="paymentMethod" required />
          </label>
          <label>
            <span>{t(k("sales.reference"))}</span>
            <input name="reference" />
          </label>
          </div>
        </form>
      </AppModal>}
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t(k("sales.collections"))}</h2>
          <button className="primary-button" type="button" onClick={() => setCreating(true)}>+ {t("administration.add")}</button>
        </div>
        <ClientRecordListControls list={list} />
        <div className="administration-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("quality.number")}</th>
                <th>{t(k("sales.receivedOn"))}</th>
                <th>{t(k("finance.amount"))}</th>
                <th>{t("planning.status")}</th>
              </tr>
            </thead>
            <tbody>
              {list.rows.map((x) => (
                <tr key={x.id}>
                  <td>{String(x.number)}</td>
                  <td>{String(x.receivedOn)}</td>
                  <td>{formatNumber(Number(x.amount))}</td>
                  <td>{String(x.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function SalesPriceListsPage({
  data,
  reload,
}: {
  data: CommercialSnapshot;
  reload: () => Promise<void>;
}) {
  const { t, formatNumber } = useLocale();
  const [error, setError] = useState<string>();
  const [creating, setCreating] = useState<"list" | "item" | null>(null);
  const lists = (data.priceLists ?? []) as CommercialRecord[];
  const items = (data.priceListItems ?? []) as CommercialRecord[];
  const list = useClientRecordList(items, "sales:priceLists");
  const submitList = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body = Object.fromEntries(new FormData(e.currentTarget));
    try {
      await saveCommercial("sales", "price-lists", undefined, body);
      e.currentTarget.reset();
      setCreating(null);
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "commercial.saveError",
      );
    }
  };
  const submitItem = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(e.currentTarget),
    );
    body.unitPrice = Number(body.unitPrice);
    body.minimumQuantity = Number(body.minimumQuantity);
    body.discountPercent = Number(body.discountPercent);
    try {
      await saveCommercial("sales", "price-list-items", undefined, body);
      e.currentTarget.reset();
      setCreating(null);
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "commercial.saveError",
      );
    }
  };
  return (
    <main className="page-content commercial-management-page">
      <Header module="sales" company={data.companyName} />
      {error && (
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      )}
      {creating === "list" && <AppModal title={t(k("sales.priceLists"))} closeLabel={t("recordNavigation.backToList")} onClose={() => setCreating(null)} saveForm wide feedback={error ? t(k(error)) : null}>
        <form className="administration-form record-form" onSubmit={submitList}>
          <div className="compact-form-grid">
          <label>
            <span>{t("aplPanel.code")}</span>
            <input name="code" required />
          </label>
          <label>
            <span>{t("aplPanel.name")}</span>
            <input name="name" required />
          </label>
          <label>
            <span>{t(k("commercial.currency"))}</span>
            <input
              name="currencyCode"
              defaultValue="EUR"
              maxLength={3}
              required
            />
          </label>
          <label>
            <span>{t(k("sales.validFrom"))}</span>
            <input name="validFrom" type="date" />
          </label>
          <label>
            <span>{t(k("sales.validTo"))}</span>
            <input name="validTo" type="date" />
          </label>
          </div>
        </form>
      </AppModal>}
      {creating === "item" && <AppModal title={t(k("sales.priceList"))} closeLabel={t("recordNavigation.backToList")} onClose={() => setCreating(null)} saveForm wide feedback={error ? t(k(error)) : null}>
        <form className="administration-form record-form" onSubmit={submitItem}>
          <div className="compact-form-grid">
          <label>
            <span>{t(k("sales.priceList"))}</span>
            <select name="priceListId" required>
              <option value="">—</option>
              {lists.map((x) => (
                <option key={x.id} value={x.id}>
                  {String(x.code)} · {String(x.name)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t(k("commercial.article"))}</span>
            <select name="articleId" required>
              <option value="">—</option>
              {data.articles.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.code} · {x.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t(k("commercial.unitPrice"))}</span>
            <input
              name="unitPrice"
              type="number"
              min="0"
              step="0.01"
              required
            />
          </label>
          <label>
            <span>{t(k("sales.minimumQuantity"))}</span>
            <input
              name="minimumQuantity"
              type="number"
              min="0.000001"
              step="any"
              defaultValue="1"
              required
            />
          </label>
          <label>
            <span>{t(k("sales.discount"))}</span>
            <input
              name="discountPercent"
              type="number"
              min="0"
              max="100"
              step="any"
              defaultValue="0"
            />
          </label>
          </div>
        </form>
      </AppModal>}
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t(k("sales.priceLists"))}</h2>
          <div className="inline-actions">
            <button className="secondary-button" type="button" onClick={() => setCreating("item")}>+ {t(k("commercial.article"))}</button>
            <button className="primary-button" type="button" onClick={() => setCreating("list")}>+ {t("administration.add")}</button>
          </div>
        </div>
        <ClientRecordListControls list={list} />
        <div className="administration-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t(k("sales.priceList"))}</th>
                <th>{t(k("commercial.article"))}</th>
                <th>{t(k("commercial.unitPrice"))}</th>
              </tr>
            </thead>
            <tbody>
              {list.rows.map((x) => (
                <tr key={x.id}>
                  <td>
                    {String(
                      lists.find((y) => y.id === x.salesPriceListId)?.code ??
                        "—",
                    )}
                  </td>
                  <td>
                    {String(
                      data.articles.find((y) => y.id === x.articleId)?.code ??
                        "—",
                    )}
                  </td>
                  <td>{formatNumber(Number(x.unitPrice))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function SalesReportsPage({ data }: { data: CommercialSnapshot }) {
  const { t, formatNumber } = useLocale();
  const invoices = (data.invoices ?? []) as CommercialRecord[];
  const customers = (data.customers ?? []) as CommercialRecord[];
  const due = (data.invoiceDueDates ?? []) as CommercialRecord[];
  const total = invoices
    .filter((x) => x.status !== "Cancelled")
    .reduce((sum, x) => sum + Number(x.totalAmount ?? 0), 0);
  const outstanding = due.reduce(
    (sum, x) =>
      sum + Math.max(0, Number(x.amount ?? 0) - Number(x.paidAmount ?? 0)),
    0,
  );
  const byCustomer = customers
    .map((customer) => ({
      customer,
      total: invoices
        .filter((x) => x.customerId === customer.id && x.status !== "Cancelled")
        .reduce((sum, x) => sum + Number(x.totalAmount ?? 0), 0),
    }))
    .filter((x) => x.total > 0)
    .sort((a, b) => b.total - a.total);
  return (
    <main className="page-content">
      <Header module="sales" company={data.companyName} />
      <section className="maintenance-kpis quality-kpis">
        <article className="indicator-card neutral maintenance-kpi">
          <span className="indicator-label">{t(k("sales.invoicedTotal"))}</span>
          <div className="indicator-value">{formatNumber(total)}</div>
        </article>
        <article className="indicator-card neutral maintenance-kpi">
          <span className="indicator-label">
            {t(k("sales.outstandingTotal"))}
          </span>
          <div className="indicator-value">{formatNumber(outstanding)}</div>
        </article>
      </section>
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t(k("sales.salesByCustomer"))}</h2>
        </div>
        <div className="administration-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("sales.customers")}</th>
                <th>{t(k("commercial.total"))}</th>
              </tr>
            </thead>
            <tbody>
              {byCustomer.map((x) => (
                <tr key={x.customer.id}>
                  <td>
                    {String(x.customer.code)} · {String(x.customer.name)}
                  </td>
                  <td>{formatNumber(x.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function PurchasingReportsPage({ data }: { data: CommercialSnapshot }) {
  const { t, formatNumber } = useLocale();
  const invoices = (data.invoices ?? []) as CommercialRecord[];
  const suppliers = (data.suppliers ?? []) as CommercialRecord[];
  const dueDates = (data.invoiceDueDates ?? []) as CommercialRecord[];
  const total = invoices
    .filter((x) => x.status !== "Cancelled")
    .reduce((sum, x) => sum + Number(x.totalAmount ?? 0), 0);
  const outstanding = dueDates.reduce(
    (sum, x) =>
      sum + Math.max(0, Number(x.amount ?? 0) - Number(x.paidAmount ?? 0)),
    0,
  );
  const bySupplier = suppliers
    .map((supplier) => ({
      supplier,
      total: invoices
        .filter((x) => x.supplierId === supplier.id && x.status !== "Cancelled")
        .reduce((sum, x) => sum + Number(x.totalAmount ?? 0), 0),
    }))
    .filter((x) => x.total > 0)
    .sort((a, b) => b.total - a.total);
  return (
    <main className="page-content">
      <Header module="purchasing" company={data.companyName} />
      <section className="maintenance-kpis quality-kpis">
        <article className="indicator-card neutral maintenance-kpi">
          <span className="indicator-label">
            {t("purchasing.invoicedTotal")}
          </span>
          <div className="indicator-value">{formatNumber(total)}</div>
        </article>
        <article className="indicator-card neutral maintenance-kpi">
          <span className="indicator-label">
            {t("purchasing.outstandingTotal")}
          </span>
          <div className="indicator-value">{formatNumber(outstanding)}</div>
        </article>
      </section>
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t("purchasing.purchasesBySupplier")}</h2>
        </div>
        <div className="administration-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("purchasing.suppliers")}</th>
                <th>{t("commercial.total")}</th>
              </tr>
            </thead>
            <tbody>
              {bySupplier.map((x) => (
                <tr key={x.supplier.id}>
                  <td>
                    {String(x.supplier.code)} · {String(x.supplier.name)}
                  </td>
                  <td>{formatNumber(x.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function PurchasingConfigurationPage({
  data,
  reload,
}: {
  data: CommercialSnapshot;
  reload: () => Promise<void>;
}) {
  const { t } = useLocale();
  const [error, setError] = useState<string>();
  const terms = (data.paymentTerms ?? []) as CommercialRecord[];
  const counters = (data.counters ?? []) as CommercialRecord[];
  const templates = (data.documentTemplates ?? []) as CommercialRecord[];
  const save = async (entity: string, event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(form),
    );
    for (const field of ["dueDays", "installments", "nextNumber", "padding"])
      if (body[field] !== undefined) body[field] = Number(body[field]);
    body.isActive = true;
    if (entity === "document-templates")
      body.showPrices = (
        form.elements.namedItem("showPrices") as HTMLInputElement
      ).checked;
    try {
      const current =
        entity === "document-templates"
          ? templates.find((x) => x.kind === body.documentKind)
          : undefined;
      await saveCommercial("purchasing", entity, current?.id, body);
      form.reset();
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "commercial.saveError",
      );
    }
  };
  return (
    <main className="page-content commercial-configuration-page">
      <Header module="purchasing" company={data.companyName} />
      {error && (
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      )}
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t("purchasing.paymentTerms")}</h2>
        </div>
        <form
          className="administration-form"
          onSubmit={(event) => void save("payment-terms", event)}
        >
          <label>
            <span>{t("aplPanel.code")}</span>
            <input name="code" required />
          </label>
          <label>
            <span>{t("aplPanel.name")}</span>
            <input name="name" required />
          </label>
          <label>
            <span>{t("purchasing.dueDays")}</span>
            <input
              name="dueDays"
              type="number"
              min="0"
              defaultValue="30"
              required
            />
          </label>
          <label>
            <span>{t("purchasing.installments")}</span>
            <input
              name="installments"
              type="number"
              min="1"
              max="60"
              defaultValue="1"
              required
            />
          </label>
          <button className="primary-button" type="submit">
            {t("administration.add")}
          </button>
        </form>
        <div className="administration-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("aplPanel.code")}</th>
                <th>{t("aplPanel.name")}</th>
                <th>{t("purchasing.dueDays")}</th>
                <th>{t("purchasing.installments")}</th>
              </tr>
            </thead>
            <tbody>
              {terms.map((x) => (
                <tr key={x.id}>
                  <td>{String(x.code)}</td>
                  <td>{String(x.name)}</td>
                  <td>{String(x.dueDays)}</td>
                  <td>{String(x.installments)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t("purchasing.documentCounters")}</h2>
        </div>
        <form
          className="administration-form"
          onSubmit={(event) => void save("counters", event)}
        >
          <input type="hidden" name="module" value="Purchasing" />
          <label>
            <span>{t("commercial.documentType")}</span>
            <select name="documentType" required>
              <option value="Requisition">
                {t("purchasing.requisitions")}
              </option>
              <option value="Quotation">{t("purchasing.quotations")}</option>
              <option value="Order">{t("purchasing.orders")}</option>
              <option value="Receipt">{t("purchasing.receipts")}</option>
              <option value="Invoice">{t("purchasing.invoices")}</option>
              <option value="Payment">{t("purchasing.payments")}</option>
            </select>
          </label>
          <label>
            <span>{t("commercial.series")}</span>
            <input name="series" required />
          </label>
          <label>
            <span>{t("sales.prefix")}</span>
            <input name="prefix" />
          </label>
          <label>
            <span>{t("commercial.nextNumber")}</span>
            <input
              name="nextNumber"
              type="number"
              min="1"
              defaultValue="1"
              required
            />
          </label>
          <label>
            <span>{t("sales.padding")}</span>
            <input
              name="padding"
              type="number"
              min="1"
              max="12"
              defaultValue="6"
              required
            />
          </label>
          <button className="primary-button" type="submit">
            {t("administration.add")}
          </button>
        </form>
        <div className="administration-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("commercial.documentType")}</th>
                <th>{t("commercial.series")}</th>
                <th>{t("sales.prefix")}</th>
                <th>{t("commercial.nextNumber")}</th>
              </tr>
            </thead>
            <tbody>
              {counters.map((x) => (
                <tr key={x.id}>
                  <td>{String(x.documentType)}</td>
                  <td>{String(x.series)}</td>
                  <td>{String(x.prefix ?? "")}</td>
                  <td>{String(x.nextNumber)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t("sales.documentTemplates")}</h2>
        </div>
        <DocumentTemplateDesigner module="purchasing" templates={templates} reload={reload} onError={setError} kinds={[
          {value:"Quotation",label:t("purchasing.quotations")},{value:"Order",label:t("purchasing.orders")},
          {value:"Receipt",label:t("purchasing.receipts")},{value:"Invoice",label:t("purchasing.invoices")}
        ]}/>
        <form
          className="administration-form"
          onSubmit={(event) => void save("document-templates", event)}
        >
          <label>
            <span>{t("sales.documentKind")}</span>
            <select name="documentKind">
              <option value="Quotation">{t("purchasing.quotations")}</option>
              <option value="Order">{t("purchasing.orders")}</option>
              <option value="Receipt">{t("purchasing.receipts")}</option>
              <option value="Invoice">{t("purchasing.invoices")}</option>
            </select>
          </label>
          <label>
            <span>{t("sales.documentTitle")}</span>
            <input name="name" required />
          </label>
          <label>
            <span>{t("sales.accentColor")}</span>
            <input name="accentColor" type="color" defaultValue="#635BFF" />
          </label>
          <label className="form-field-wide">
            <span>{t("sales.headerText")}</span>
            <textarea name="headerText" />
          </label>
          <label className="form-field-wide">
            <span>{t("sales.footerText")}</span>
            <textarea name="footerText" />
          </label>
          <label className="checkbox-label">
            <input name="showPrices" type="checkbox" defaultChecked />
            <span>{t("sales.showPrices")}</span>
          </label>
          <button className="primary-button" type="submit">
            {t("administration.save")}
          </button>
        </form>
        <div className="administration-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("sales.documentKind")}</th>
                <th>{t("sales.documentTitle")}</th>
                <th>{t("sales.accentColor")}</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((x) => (
                <tr key={x.id}>
                  <td>{String(x.kind)}</td>
                  <td>{String(x.title)}</td>
                  <td>{String(x.accentColor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function SalesFiscalConfigurationPage({
  data,
  reload,
}: {
  data: CommercialSnapshot;
  reload: () => Promise<void>;
}) {
  const { t } = useLocale();
  const [error, setError] = useState<string>();
  const fiscal = (data.fiscalConfiguration ?? {}) as CommercialRecord;
  const counters = (data.counters ?? []) as CommercialRecord[];
  const templates = (data.documentTemplates ?? []) as CommercialRecord[];
  const submitFiscal = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(e.currentTarget),
    );
    body.isProduction = (
      e.currentTarget.elements.namedItem("isProduction") as HTMLInputElement
    ).checked;
    try {
      await saveCommercial("sales", "fiscal-configuration", undefined, body);
      await reload();
    } catch (x) {
      setError(x instanceof Error ? x.message : "commercial.saveError");
    }
  };
  const submitCounter = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(e.currentTarget),
    );
    body.dueDays = Number(body.dueDays);
    body.installments = Number(body.installments);
    try {
      await saveCommercial("sales", "counters", undefined, body);
      e.currentTarget.reset();
      await reload();
    } catch (x) {
      setError(x instanceof Error ? x.message : "commercial.saveError");
    }
  };
  const submitTemplate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(e.currentTarget),
    );
    body.showPrices = (
      e.currentTarget.elements.namedItem("showPrices") as HTMLInputElement
    ).checked;
    body.isActive = true;
    const current = templates.find((x) => x.kind === body.documentKind);
    try {
      await saveCommercial("sales", "document-templates", current?.id, body);
      await reload();
    } catch (x) {
      setError(x instanceof Error ? x.message : "commercial.saveError");
    }
  };
  return (
    <main className="page-content commercial-configuration-page">
      <header className="welcome administration-heading">
        <div>
          <span className="eyebrow">{t("sales.eyebrow")}</span>
          <h1>{t("sales.configuration")}</h1>
          <p>{t(k("sales.fiscalConfigurationHelp"))}</p>
        </div>
      </header>
      {error && (
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      )}
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t(k("sales.fiscalIdentity"))}</h2>
        </div>
        <form className="administration-form" onSubmit={submitFiscal}>
          <label>
            <span>{t(k("sales.legalName"))}</span>
            <input
              name="legalName"
              defaultValue={String(fiscal.legalName ?? "")}
              required
            />
          </label>
          <label>
            <span>{t(k("sales.taxIdentifier"))}</span>
            <input
              name="taxIdentifier"
              defaultValue={String(fiscal.taxIdentifier ?? "")}
              required
            />
          </label>
          <label className="form-field-wide">
            <span>{t(k("sales.fiscalAddress"))}</span>
            <textarea
              name="fiscalAddress"
              defaultValue={String(fiscal.fiscalAddress ?? "")}
              required
            />
          </label>
          <label>
            <span>{t(k("sales.countryCode"))}</span>
            <input
              name="countryCode"
              maxLength={2}
              defaultValue={String(fiscal.countryCode ?? "ES")}
              required
            />
          </label>
          <label>
            <span>{t(k("sales.fiscalSystem"))}</span>
            <select
              name="fiscalSystem"
              defaultValue={String(fiscal.fiscalSystem ?? "None")}
            >
              <option value="None">{t(k("sales.fiscalNone"))}</option>
              <option value="Sii">SII</option>
              <option value="VeriFactu">VERI*FACTU</option>
            </select>
          </label>
          <label>
            <span>{t(k("sales.certificateReference"))}</span>
            <input
              name="certificateReference"
              defaultValue={String(fiscal.veriFactuCertificateReference ?? "")}
            />
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isProduction"
              defaultChecked={Boolean(fiscal.isProduction)}
            />
            <span>{t(k("sales.productionEnvironment"))}</span>
          </label>
          <button className="primary-button" type="submit">
            {t("administration.save")}
          </button>
        </form>
      </section>
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t(k("sales.invoiceSeries"))}</h2>
        </div>
        <form className="administration-form" onSubmit={submitCounter}>
          <label>
            <span>{t("commercial.series")}</span>
            <input name="series" required />
          </label>
          <label>
            <span>{t(k("sales.prefix"))}</span>
            <input name="code" />
          </label>
          <label>
            <span>{t("commercial.nextNumber")}</span>
            <input
              type="number"
              min="1"
              name="dueDays"
              defaultValue="1"
              required
            />
          </label>
          <label>
            <span>{t(k("sales.padding"))}</span>
            <input
              type="number"
              min="1"
              max="12"
              name="installments"
              defaultValue="6"
              required
            />
          </label>
          <button className="primary-button" type="submit">
            {t("administration.add")}
          </button>
        </form>
        <div className="administration-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("commercial.series")}</th>
                <th>{t(k("sales.prefix"))}</th>
                <th>{t("commercial.nextNumber")}</th>
              </tr>
            </thead>
            <tbody>
              {counters.map((x) => (
                <tr key={x.id}>
                  <td>{String(x.series)}</td>
                  <td>{String(x.prefix ?? "")}</td>
                  <td>{String(x.nextNumber)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t(k("sales.documentTemplates"))}</h2>
        </div>
        <DocumentTemplateDesigner module="sales" templates={templates} reload={reload} onError={setError} kinds={[
          {value:"Quotation",label:t("sales.quotations")},{value:"Order",label:t("sales.orders")},
          {value:"Delivery",label:t("sales.deliveries")},{value:"Invoice",label:t("sales.invoices")}
        ]}/>
        <form className="administration-form" onSubmit={submitTemplate}>
          <label>
            <span>{t(k("sales.documentKind"))}</span>
            <select name="documentKind">
              <option value="Quotation">{t("sales.quotations")}</option>
              <option value="Order">{t("sales.orders")}</option>
              <option value="Delivery">{t("sales.deliveries")}</option>
              <option value="Invoice">{t("sales.invoices")}</option>
            </select>
          </label>
          <label>
            <span>{t(k("sales.documentTitle"))}</span>
            <input name="name" required />
          </label>
          <label>
            <span>{t(k("sales.accentColor"))}</span>
            <input name="accentColor" type="color" defaultValue="#635BFF" />
          </label>
          <label className="form-field-wide">
            <span>{t(k("sales.headerText"))}</span>
            <textarea name="headerText" />
          </label>
          <label className="form-field-wide">
            <span>{t(k("sales.footerText"))}</span>
            <textarea name="footerText" />
          </label>
          <label className="checkbox-label">
            <input name="showPrices" type="checkbox" defaultChecked />
            <span>{t(k("sales.showPrices"))}</span>
          </label>
          <button className="primary-button" type="submit">
            {t("administration.save")}
          </button>
        </form>
        <div className="administration-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t(k("sales.documentKind"))}</th>
                <th>{t(k("sales.documentTitle"))}</th>
                <th>{t(k("sales.accentColor"))}</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((x) => (
                <tr key={x.id}>
                  <td>{String(x.kind)}</td>
                  <td>{String(x.title)}</td>
                  <td>{String(x.accentColor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function SalesCustomersPage({
  data,
  reload,
}: {
  data: CommercialSnapshot;
  reload: () => Promise<void>;
}) {
  const { t } = useLocale();
  const [editing, setEditing] = useState<CommercialRecord | null | undefined>();
  const [customerTab, setCustomerTab] = useState<"details" | "contacts" | "addresses">("details");
  const [error, setError] = useState<string>();
  const customers = (data.customers ?? []) as CommercialRecord[];
  const list = useClientRecordList(customers, "sales:customers");
  const contacts = ((data.customerContacts ?? []) as CommercialRecord[]).filter(x => x.customerId === editing?.id);
  const addresses = ((data.customerAddresses ?? []) as CommercialRecord[]).filter(x => x.customerId === editing?.id);
  const contactList = useClientRecordList(contacts, `sales:customer:${editing?.id ?? "new"}:contacts`);
  const addressList = useClientRecordList(addresses, `sales:customer:${editing?.id ?? "new"}:addresses`);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(e.currentTarget),
    );
    body.creditLimit = Number(body.creditLimit ?? 0);
    body.isActive = (
      e.currentTarget.elements.namedItem("isActive") as HTMLInputElement
    ).checked;
    try {
      await saveCommercial("sales", "customers", editing?.id, body);
      setEditing(undefined);
      await reload();
    } catch (x) {
      setError(x instanceof Error ? x.message : "commercial.saveError");
    }
  };
  const submitStructure = async (section: "customerContacts" | "customerAddresses", e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editing?.id) return;
    const body: Record<string, unknown> = Object.fromEntries(new FormData(e.currentTarget));
    body.parentId = editing.id;
    body.isDefault = (e.currentTarget.elements.namedItem("isDefault") as HTMLInputElement | null)?.checked ?? false;
    try {
      await saveCommercial("sales", section === "customerContacts" ? "customer-contacts" : "customer-addresses", undefined, body);
      e.currentTarget.reset();
      setError(undefined);
      await reload();
    } catch (x) {
      setError(x instanceof Error ? x.message : "commercial.saveError");
    }
  };
  if (editing !== undefined) {
    const recordIndex = editing ? list.rows.findIndex((record) => record.id === editing.id) : -1;
    return (
      <main className="page-content">
        <AppModal title={t("sales.customers")} closeLabel={t("recordNavigation.backToList")} onClose={() => setEditing(undefined)} onPrevious={recordIndex > 0 ? () => { setCustomerTab("details"); setEditing(list.rows[recordIndex - 1]); } : null} onNext={recordIndex >= 0 && recordIndex < list.rows.length - 1 ? () => { setCustomerTab("details"); setEditing(list.rows[recordIndex + 1]); } : null} saveForm={customerTab === "details"} wide feedback={error ? t(k(error)) : null}>
        <nav className="administration-tabs inventory-tabs" aria-label={t("sales.customers")}>
          <button className={customerTab === "details" ? "active" : ""} type="button" onClick={() => setCustomerTab("details")}>{t("sales.customers")}</button>
          {editing && <button className={customerTab === "contacts" ? "active" : ""} type="button" onClick={() => setCustomerTab("contacts")}>{t(k("sales.customerContacts"))}</button>}
          {editing && <button className={customerTab === "addresses" ? "active" : ""} type="button" onClick={() => setCustomerTab("addresses")}>{t(k("sales.customerAddresses"))}</button>}
        </nav>
        {customerTab === "details" && (
          <form
            id="sales-customer-form"
            className="administration-form record-form"
            onSubmit={submit}
          >
            <div className="compact-form-grid">
            <label>
              <span>{t("aplPanel.code")}</span>
              <input
                name="code"
                defaultValue={String(editing?.code ?? "")}
                required={!editing}
              />
            </label>
            <label>
              <span>{t("aplPanel.name")}</span>
              <input
                name="name"
                defaultValue={String(editing?.name ?? "")}
                required
              />
            </label>
            <label>
              <span>{t(k("sales.taxIdentifier"))}</span>
              <input
                name="taxIdentifier"
                defaultValue={String(editing?.taxIdentifier ?? "")}
                required
              />
            </label>
            <label>
              <span>{t(k("sales.email"))}</span>
              <input
                type="email"
                name="email"
                defaultValue={String(editing?.email ?? "")}
              />
            </label>
            <label>
              <span>{t(k("sales.phone"))}</span>
              <input name="phone" defaultValue={String(editing?.phone ?? "")} />
            </label>
            <label>
              <span>{t(k("commercial.currency"))}</span>
              <input
                name="currencyCode"
                maxLength={3}
                defaultValue={String(editing?.currencyCode ?? "EUR")}
                required
              />
            </label>
            <label>
              <span>{t(k("sales.creditLimit"))}</span>
              <input
                type="number"
                min="0"
                step="any"
                name="creditLimit"
                defaultValue={String(editing?.creditLimit ?? 0)}
              />
            </label>
            <label>
              <span>{t(k("sales.paymentTerms"))}</span>
              <input
                name="paymentTerms"
                defaultValue={String(editing?.paymentTerms ?? "")}
              />
            </label>
            <label className="form-field-wide">
              <span>{t(k("sales.billingAddress"))}</span>
              <textarea
                name="billingAddress"
                defaultValue={String(editing?.billingAddress ?? "")}
                required
              />
            </label>
            <label className="form-field-wide">
              <span>{t(k("sales.shippingAddress"))}</span>
              <textarea
                name="shippingAddress"
                defaultValue={String(editing?.shippingAddress ?? "")}
              />
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                defaultChecked={editing?.isActive !== false}
              />
              <span>{t("administration.active")}</span>
            </label>
            </div>
          </form>
        )}
        {customerTab === "contacts" && editing && (
          <section className="inventory-detail-panel">
            <form className="administration-form inventory-inline-form" onSubmit={(event) => void submitStructure("customerContacts", event)}>
              <div className="compact-form-grid">
                <label><span>{t("aplPanel.name")}</span><input name="name" required /></label>
                <label><span>{t(k("sales.position"))}</span><input name="position" /></label>
                <label><span>{t(k("sales.email"))}</span><input name="email" type="email" required /></label>
                <label><span>{t(k("sales.phone"))}</span><input name="phone" /></label>
                <label className="checkbox-label"><input type="checkbox" name="isDefault" /><span>{t(k("sales.primaryOrDefault"))}</span></label>
              </div>
              <div className="form-actions"><button className="primary-button" type="submit">{t("administration.add")}</button></div>
            </form>
            <ClientRecordListControls list={contactList} />
            <div className="administration-table-wrap"><table className="data-table administration-table"><thead><tr><th>{t("aplPanel.name")}</th><th>{t(k("sales.position"))}</th><th>{t(k("sales.email"))}</th><th>{t(k("sales.phone"))}</th></tr></thead><tbody>{contactList.rows.map(x => <tr key={x.id}><td>{String(x.name)}</td><td>{String(x.position ?? "—")}</td><td>{String(x.email ?? "—")}</td><td>{String(x.phone ?? "—")}</td></tr>)}</tbody></table></div>
          </section>
        )}
        {customerTab === "addresses" && editing && (
          <section className="inventory-detail-panel">
            <form className="administration-form inventory-inline-form" onSubmit={(event) => void submitStructure("customerAddresses", event)}>
              <div className="compact-form-grid">
                <label><span>{t("aplPanel.code")}</span><input name="code" required /></label>
                <label><span>{t(k("sales.addressKind"))}</span><select name="addressKind"><option value="Billing">{t(k("sales.billing"))}</option><option value="Shipping">{t(k("sales.shipping"))}</option><option value="Other">{t(k("sales.other"))}</option></select></label>
                <label><span>{t("aplPanel.name")}</span><input name="name" required /></label>
                <label className="form-field-wide"><span>{t(k("sales.addressLine"))}</span><input name="addressLine" required /></label>
                <label><span>{t(k("sales.postalCode"))}</span><input name="postalCode" /></label>
                <label><span>{t(k("sales.city"))}</span><input name="city" /></label>
                <label><span>{t(k("sales.province"))}</span><input name="province" /></label>
                <label><span>{t(k("sales.countryCode"))}</span><input name="countryCode" defaultValue="ES" maxLength={2} required /></label>
                <label className="checkbox-label"><input type="checkbox" name="isDefault" /><span>{t(k("sales.primaryOrDefault"))}</span></label>
              </div>
              <div className="form-actions"><button className="primary-button" type="submit">{t("administration.add")}</button></div>
            </form>
            <ClientRecordListControls list={addressList} />
            <div className="administration-table-wrap"><table className="data-table administration-table"><thead><tr><th>{t("aplPanel.code")}</th><th>{t("aplPanel.name")}</th><th>{t(k("sales.addressKind"))}</th><th>{t(k("sales.city"))}</th></tr></thead><tbody>{addressList.rows.map(x => <tr key={x.id}><td>{String(x.code)}</td><td>{String(x.name)}</td><td>{String(x.addressKind)}</td><td>{String(x.city ?? "—")}</td></tr>)}</tbody></table></div>
          </section>
        )}
        </AppModal>
      </main>
    );
  }
  return (
    <main className="page-content">
      <Header module="sales" company={data.companyName} />
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t("sales.customers")}</h2>
          <button
            className="primary-button"
            type="button"
            onClick={() => { setCustomerTab("details"); setEditing(null); }}
          >
            + {t("administration.add")}
          </button>
        </div>
        <ClientRecordListControls list={list} />
        <div className="administration-table-wrap">
          <table className="data-table administration-table">
            <thead>
              <tr>
                <SortableHeader
                  label={t("aplPanel.code")}
                  active={list.sort === "code"}
                  direction={list.direction}
                  onClick={() => list.changeSort("code")}
                />
                <SortableHeader
                  label={t("aplPanel.name")}
                  active={list.sort === "name"}
                  direction={list.direction}
                  onClick={() => list.changeSort("name")}
                />
                <SortableHeader
                  label={t(k("sales.taxIdentifier"))}
                  active={list.sort === "taxIdentifier"}
                  direction={list.direction}
                  onClick={() => list.changeSort("taxIdentifier")}
                />
                <SortableHeader
                  label={t(k("sales.email"))}
                  active={list.sort === "email"}
                  direction={list.direction}
                  onClick={() => list.changeSort("email")}
                />
              </tr>
            </thead>
            <tbody>
              {list.rows.map((x) => (
                <tr key={x.id}>
                  <td>
                    <button
                      className="record-code-link"
                      onClick={() => { setCustomerTab("details"); setEditing(x); }}
                    >
                      {String(x.code)}
                    </button>
                  </td>
                  <td>{String(x.name)}</td>
                  <td>{String(x.taxIdentifier ?? "—")}</td>
                  <td>{String(x.email ?? "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
